import { type ReactNode, type Dispatch, type SetStateAction, useState, useRef, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Plus, Layers, Check, Loader2, X, GripVertical,
  RefreshCw, AlertTriangle, CheckCircle2, Building2, ChevronDown, ChevronRight, Trash2, Sparkles, ChevronsDown, Info,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { DataTable, EmptyState, ErrorState, type ColumnDef, Button, LoadingState, Tooltip } from '@/components/ui'
import type { ArticleGroup } from '../shared.ts'
import { getErrorMessage } from '../shared.ts'

// ─── Missing Articles Panel ──────────────────────────────────────────────────

interface SapTenantSimple {
  id: string
  code: string
  name: string
}

interface MissingData {
  localTotal: number
  configuredGroupCount: number
  tenants: Array<{
    tenantId: string
    tenantCode: string
    tenantName: string
    syncedCount: number
    missingCount: number
    missing: Array<{ id: string; itemCode: string; itemName: string; groupName: string | null }>
  }>
}

interface PushStartedStatus {
  running: boolean
}

export function MissingArticlesPanel({ onPushStarted }: { onPushStarted?: (status: PushStartedStatus) => void }) {
  const queryClient = useQueryClient()
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])

  const { data, isLoading, refetch } = useQuery<MissingData>({
    queryKey: ['articles-missing-sync'],
    queryFn: () => apiClient.get('/articles/missing-sync').then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: availableTenants = [] } = useQuery<SapTenantSimple[]>({
    queryKey: ['article-groups-sap-tenants'],
    queryFn: () => apiClient.get('/articles/groups/sap-tenants').then((r) => r.data),
    staleTime: 300_000,
  })

  const bulkAssignMutation = useMutation({
    mutationFn: (tenantIds: string[]) =>
      apiClient.post('/articles/groups/bulk-assign-tenants', { tenantIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      queryClient.invalidateQueries({ queryKey: ['articles-missing-sync'] })
    },
  })

  const pushMutation = useMutation({
    mutationFn: () => apiClient.post('/articles/push-missing', {}),
    onSuccess: (res) => {
      onPushStarted?.((res as any).data)
    },
  })

  if (isLoading) return <LoadingState />

  const configuredGroupCount = data?.configuredGroupCount ?? 0
  const nothingConfigured = configuredGroupCount === 0
  const totalMissing = data?.tenants.reduce((s, t) => s + t.missingCount, 0) ?? 0

  return (
    <div className="space-y-4">

      {/* Bulk-Zuweisung wenn noch nichts konfiguriert */}
      {nothingConfigured && availableTenants.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
          <div>
            <p className="text-sm font-medium text-blue-900">Keine Warengruppen konfiguriert</p>
            <p className="text-xs text-blue-700 mt-0.5">
              Wähle Mandanten aus und weise sie allen Warengruppen auf einmal zu.
              Danach wird hier angezeigt, welche Artikel noch fehlen.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {availableTenants.map((t) => {
              const active = selectedTenants.includes(t.id)
              return (
                <button
                  key={t.id}
                  onClick={() =>
                    setSelectedTenants((prev) =>
                      active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                    )
                  }
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400',
                  )}
                >
                  <Building2 className="w-3 h-3" />
                  {t.code} — {t.name}
                </button>
              )
            })}
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={selectedTenants.length === 0 || bulkAssignMutation.isPending}
            onClick={() => bulkAssignMutation.mutate(selectedTenants)}
          >
            {bulkAssignMutation.isPending
              ? 'Weise zu…'
              : `Alle Warengruppen → ${selectedTenants.length > 0 ? availableTenants.filter((t) => selectedTenants.includes(t.id)).map((t) => t.code).join(' + ') : '…'}`}
          </Button>
          {bulkAssignMutation.isSuccess && (
            <p className="text-xs text-green-700">
              ✓ {(bulkAssignMutation.data as any)?.data?.updated} Warengruppen aktualisiert.
            </p>
          )}
        </div>
      )}

      {/* Status-Zeile */}
      <div className="flex items-center justify-between">
        <div>
          {nothingConfigured ? (
            <p className="text-sm text-gray-500 italic">Noch keine Sync-Mandanten konfiguriert.</p>
          ) : totalMissing === 0 ? (
            <p className="text-sm text-green-700 font-medium">
              ✓ Alle Artikel sind in den konfigurierten Mandanten vorhanden.
            </p>
          ) : (
            <p className="text-sm text-amber-700 font-medium">
              {totalMissing} Artikel fehlen in mindestens einem Sync-Mandanten.
            </p>
          )}
          {!nothingConfigured && (
            <p className="text-xs text-gray-400 mt-0.5">
              {configuredGroupCount} Warengruppen mit Sync-Mandanten konfiguriert.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()}>Aktualisieren</Button>
          {totalMissing > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => pushMutation.mutate()}
              disabled={pushMutation.isPending}
            >
              {pushMutation.isPending ? 'Pushe…' : `${totalMissing} Artikel pushen`}
            </Button>
          )}
        </div>
      </div>

      {/* Per-Tenant-Uebersicht */}
      {!nothingConfigured && data && data.tenants.some((t) => t.missingCount > 0) && (
        <div className="space-y-3">
          {data.tenants.filter((t) => t.missingCount > 0).map((t) => (
            <div key={t.tenantId} className="rounded-lg border border-amber-200 overflow-hidden">
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">{t.tenantCode}</span>
                <span className="text-xs text-amber-600">— {t.missingCount} fehlende Artikel</span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Artikelnr.</th>
                    <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Bezeichnung</th>
                    <th className="text-left px-3 py-1.5 font-semibold text-gray-500">Warengruppe</th>
                  </tr>
                </thead>
                <tbody>
                  {t.missing.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-mono text-gray-700">{r.itemCode}</td>
                      <td className="px-3 py-1.5 text-gray-900 truncate max-w-[280px]">{r.itemName}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.groupName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface SapTenant {
  id: string
  code: string
  name: string
}

interface SapGroupEntry {
  sapCode: number
  sapName: string
  localGroupId: string | null
  localGroupCode: number | null
  localName: string | null
  expectedSapName: string | null
  status: 'ok' | 'name_mismatch' | 'orphaned' | 'missing_in_sap'
  articleCount: number
}

// ─── TANSS Type Labels ──────────────────────────────────────────────────────

const TANSS_LINK_TYPE_LABELS: Record<number, string> = {
  1: 'PC',
  2: 'Server',
  3: 'Peripherie',
  4: 'Komponente',
}

const TANSS_LINK_TYPE_COLORS: Record<number, string> = {
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-purple-100 text-purple-700',
  3: 'bg-green-100 text-green-700',
  4: 'bg-orange-100 text-orange-700',
}

// ─── Searchable group combobox ────────────────────────────────────────────────

function GroupSearchSelect({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: string; groupCode: number; name: string }>
  value: number | ''
  onChange: (code: number | '') => void
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const selected = options.find(o => o.groupCode === value)
  const filtered = options.filter(o => {
    const q = search.toLowerCase()
    return !q || String(o.groupCode).includes(q) || o.name.toLowerCase().includes(q)
  })

  return (
    <div ref={ref} className="relative w-full">
      <div
        className="flex items-center w-full border border-gray-300 rounded bg-white px-1.5 py-1 text-xs cursor-text"
        onClick={() => setOpen(true)}
      >
        {open ? (
          <input
            autoFocus
            className="flex-1 outline-none text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
          />
        ) : (
          <span className={cn('flex-1 truncate', !selected && 'text-gray-400')}>
            {selected ? `${selected.groupCode} – ${selected.name}` : '— Gruppe wählen —'}
          </span>
        )}
      </div>
      {open && (
        <div className="absolute z-50 mt-0.5 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg text-xs">
          {filtered.length === 0 && <div className="px-3 py-2 text-gray-400">Keine Treffer</div>}
          {filtered.map(o => (
            <div
              key={o.id}
              className={cn('px-3 py-1.5 cursor-pointer hover:bg-brand-50', o.groupCode === value && 'bg-brand-100 font-medium')}
              onMouseDown={(e) => { e.preventDefault(); onChange(o.groupCode as number); setSearch(''); setOpen(false) }}
            >
              <span className="font-mono text-gray-400 mr-1.5">{o.groupCode}</span>{o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── SAP Sync types ──────────────────────────────────────────────────────────

interface SapStepSyncResult {
  sync: { synced: number; renamed: number; skipped: number }
  cleanup: { deleted: number; reassigned: number; failed: number; renamed: number; details: Array<{ code: number; name: string; action: string }> }
  comparison: {
    sapGroups: SapGroupEntry[]
    missingInSap: SapGroupEntry[]
    summary: { total: number; ok: number; nameMismatch: number; orphaned: number; missingInSap: number }
  }
  perTenant: Array<{
    tenantId: string
    tenantCode: string
    sync: { synced: number; renamed: number; skipped: number }
    cleanup: { deleted: number; reassigned: number; failed: number; renamed: number; details: Array<{ code: number; name: string; action: string }> }
    comparison: {
      sapGroups: SapGroupEntry[]
      missingInSap: SapGroupEntry[]
      summary: { total: number; ok: number; nameMismatch: number; orphaned: number; missingInSap: number }
    }
    errors: string[]
  }>
  errors: string[]
}

type SapSyncPhase =
  | { status: 'idle' }
  | { status: 'running'; tenantIdx: number; step: 'sync' | 'cleanup' | 'compare'; tenantCode: string; tenantCount: number; syncProgress?: { processed: number; total: number } }
  | { status: 'done'; result: SapStepSyncResult }
  | { status: 'error'; message: string }

const SAP_SYNC_STEP_LABELS: Record<string, string> = {
  sync: 'Gruppen synchronisieren',
  cleanup: 'Verwaiste bereinigen',
  compare: 'Abgleich',
}

// ─── SAP Defaults: shared edit row + read chips ──────────────────────────────

const SAP_ITEM_TYPE_LABELS: Record<string, string> = {
  itItems: 'Ware',
  itLabor: 'Arbeit',
  itTravel: 'Reise',
  itFixedAssets: 'Anlagegut',
}

function SapDefaultsEditRow({
  editForm,
  setEditForm,
  uomGroupOptions,
}: {
  editForm: Partial<ArticleGroup>
  setEditForm: Dispatch<SetStateAction<Partial<ArticleGroup>>>
  uomGroupOptions: Array<{ value: number; label: string }>
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs border-t border-dashed border-gray-200 pt-2 mt-1">
      <span className="text-gray-400 font-medium shrink-0">SAP-Defaults:</span>
      <label className="flex items-center gap-1">
        <span className="text-gray-600">Artikelart:</span>
        <select
          className="border border-gray-300 rounded px-1 py-0.5 text-xs"
          value={editForm.defaultSapItemType ?? ''}
          onChange={(e) => setEditForm((f) => ({ ...f, defaultSapItemType: e.target.value || null }))}
        >
          <option value="">— kein Default —</option>
          {Object.entries(SAP_ITEM_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-1">
        <span className="text-gray-600">ME-Gruppe:</span>
        <select
          className="border border-gray-300 rounded px-1 py-0.5 text-xs w-36"
          value={editForm.defaultSapUomGroupEntry ?? ''}
          onChange={(e) => setEditForm((f) => ({ ...f, defaultSapUomGroupEntry: e.target.value ? Number(e.target.value) : null }))}
        >
          <option value="">— kein Default —</option>
          {uomGroupOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          checked={editForm.defaultSapInventoryItem ?? false}
          onChange={(e) => setEditForm((f) => ({ ...f, defaultSapInventoryItem: e.target.checked ? true : null }))}
        />
        <span className="text-gray-600">Lager</span>
      </label>
      <label className="flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          checked={editForm.defaultSapPurchaseItem ?? false}
          onChange={(e) => setEditForm((f) => ({ ...f, defaultSapPurchaseItem: e.target.checked ? true : null }))}
        />
        <span className="text-gray-600">Einkauf</span>
      </label>
      <label className="flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          checked={editForm.defaultSapSalesItem ?? false}
          onChange={(e) => setEditForm((f) => ({ ...f, defaultSapSalesItem: e.target.checked ? true : null }))}
        />
        <span className="text-gray-600">Verkauf</span>
      </label>
    </div>
  )
}

function SapDefaultsChips({ group }: { group: ArticleGroup }) {
  const hasDefaults = group.defaultSapItemType || group.defaultSapUomGroupEntry != null
    || group.defaultSapInventoryItem != null || group.defaultSapPurchaseItem != null || group.defaultSapSalesItem != null
  if (!hasDefaults) return null
  return (
    <span className="flex items-center gap-1 ml-1">
      {group.defaultSapItemType && (
        <span className="text-[10px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 font-medium">
          {SAP_ITEM_TYPE_LABELS[group.defaultSapItemType] ?? group.defaultSapItemType}
        </span>
      )}
      {group.defaultSapUomGroupEntry != null && (
        <span className="text-[10px] px-1 py-0.5 rounded bg-sky-50 text-sky-600 font-medium">
          ME#{group.defaultSapUomGroupEntry}
        </span>
      )}
      {group.defaultSapInventoryItem && <span className="text-[10px] px-1 py-0.5 rounded bg-green-50 text-green-600 font-medium">Lager</span>}
      {group.defaultSapPurchaseItem && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">EK</span>}
      {group.defaultSapSalesItem && <span className="text-[10px] px-1 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">VK</span>}
    </span>
  )
}

// ─── Article Groups (Sectioned List) ────────────────────────────────────────

export function ArticleGroupsSection() {
  const queryClient = useQueryClient()
  const { data: groups = [], isLoading, isError: groupsIsError, error: groupsError } = useQuery<ArticleGroup[]>({
    queryKey: ['article-groups'],
    queryFn: () => apiClient.get('/articles/groups').then((r) => r.data),
    retry: false,
  })

  const { data: tenantSapMap = {} } = useQuery<Record<string, Array<{ tenantCode: string; sapCode: number }>>>({
    queryKey: ['article-groups-tenant-sap-map'],
    queryFn: () => apiClient.get('/articles/groups/tenant-sap-map').then((r) => r.data),
    retry: false,
    staleTime: 60_000,
  })

  const { data: sapTenants = [] } = useQuery<SapTenant[]>({
    queryKey: ['article-groups-sap-tenants'],
    queryFn: () => apiClient.get('/articles/groups/sap-tenants').then((r) => r.data),
    retry: false,
  })

  const firstTenantId = sapTenants[0]?.id
  const { data: uomGroups = [] } = useQuery<Array<{ AbsEntry: number; Code?: string; Name?: string }>>({
    queryKey: ['article-groups-uom-groups', firstTenantId],
    queryFn: () => apiClient.get('/articles/groups/uom-groups', { headers: { 'x-tenant-id': firstTenantId! } }).then((r) => r.data),
    enabled: !!firstTenantId,
    staleTime: 300_000,
  })

  const uomGroupOptions = useMemo(() =>
    uomGroups.map((g) => ({ value: g.AbsEntry, label: g.Name ? `${g.AbsEntry} – ${g.Name}` : String(g.AbsEntry) })),
    [uomGroups],
  )

  const [sapSyncPhase, setSapSyncPhase] = useState<SapSyncPhase>({ status: 'idle' })

  async function runSapSync() {
    const tenants = sapTenants
    if (tenants.length === 0) {
      setSapSyncPhase({ status: 'error', message: 'Kein SAP-Mandant konfiguriert' })
      return
    }
    setReassignedSapCodes(new Set())
    setRenamedSapCodes(new Set())
    setSapSyncPhase({ status: 'running', tenantIdx: 0, step: 'sync', tenantCode: tenants[0].code, tenantCount: tenants.length })

    const SYNC_BATCH_SIZE = 25
    const perTenant: SapStepSyncResult['perTenant'] = []
    for (let i = 0; i < tenants.length; i++) {
      const tenant = tenants[i]
      try {
        // Sync step: paginated loop — each request processes at most SYNC_BATCH_SIZE groups
        // to stay within Passenger/Nginx timeout limits (~60s per request).
        setSapSyncPhase({ status: 'running', tenantIdx: i, step: 'sync', tenantCode: tenant.code, tenantCount: tenants.length })
        let syncOffset = 0
        let syncTotal = 0
        const syncResult = { synced: 0, renamed: 0, skipped: 0, errors: [] as string[] }
        let complete = false
        do {
          setSapSyncPhase({ status: 'running', tenantIdx: i, step: 'sync', tenantCode: tenant.code, tenantCount: tenants.length, syncProgress: syncTotal ? { processed: syncOffset, total: syncTotal } : undefined })
          const chunk = await apiClient.post('/articles/groups/sap-sync-step', { tenantId: tenant.id, step: 'sync', offset: syncOffset, limit: SYNC_BATCH_SIZE }, { timeout: 90000 }).then(r => r.data)
          syncResult.synced += chunk.synced ?? 0
          syncResult.renamed += chunk.renamed ?? 0
          syncResult.skipped += chunk.skipped ?? 0
          syncResult.errors.push(...(chunk.errors ?? []))
          complete = chunk.complete ?? true
          syncTotal = chunk.totalPlanEntries ?? syncTotal
          syncOffset = chunk.nextOffset ?? (syncOffset + SYNC_BATCH_SIZE)
        } while (!complete)

        setSapSyncPhase({ status: 'running', tenantIdx: i, step: 'cleanup', tenantCode: tenant.code, tenantCount: tenants.length })
        const cleanupResult = await apiClient.post('/articles/groups/sap-sync-step', { tenantId: tenant.id, step: 'cleanup' }, { timeout: 120000 }).then(r => r.data)

        setSapSyncPhase({ status: 'running', tenantIdx: i, step: 'compare', tenantCode: tenant.code, tenantCount: tenants.length })
        const compareResult = await apiClient.post('/articles/groups/sap-sync-step', { tenantId: tenant.id, step: 'compare' }, { timeout: 60000 }).then(r => r.data)

        perTenant.push({
          tenantId: tenant.id,
          tenantCode: tenant.code,
          sync: syncResult,
          cleanup: cleanupResult,
          comparison: compareResult,
          errors: [...syncResult.errors, ...(cleanupResult.errors ?? [])],
        })
      } catch (err) {
        setSapSyncPhase({ status: 'error', message: getErrorMessage(err as Error) })
        return
      }
    }

    const sync = perTenant.reduce(
      (acc, t) => ({ synced: acc.synced + t.sync.synced, renamed: acc.renamed + t.sync.renamed, skipped: acc.skipped + t.sync.skipped }),
      { synced: 0, renamed: 0, skipped: 0 },
    )
    const cleanup = perTenant.reduce(
      (acc, t) => ({ deleted: acc.deleted + t.cleanup.deleted, reassigned: acc.reassigned + t.cleanup.reassigned, failed: acc.failed + t.cleanup.failed, renamed: acc.renamed + t.cleanup.renamed, details: [...acc.details, ...t.cleanup.details] }),
      { deleted: 0, reassigned: 0, failed: 0, renamed: 0, details: [] as Array<{ code: number; name: string; action: string }> },
    )
    const comparison = perTenant[0]?.comparison ?? { sapGroups: [], missingInSap: [], summary: { total: 0, ok: 0, nameMismatch: 0, orphaned: 0, missingInSap: 0 } }
    const allErrors = perTenant.flatMap(t => t.errors.map(e => t.tenantCode ? `[${t.tenantCode}] ${e}` : e))

    setSapSyncPhase({ status: 'done', result: { sync, cleanup, comparison, perTenant, errors: allErrors } })
    queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    queryClient.invalidateQueries({ queryKey: ['article-groups-tenant-sap-map'] })
  }

  const seedMutation = useMutation<{ created: number; updated: number; merged: number; cleaned: number }, Error>({
    mutationFn: () => apiClient.post('/articles/groups/seed', {}).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article-groups'] }),
  })

  const [reassignedSapCodes, setReassignedSapCodes] = useState<Set<number>>(new Set())
  const [renamedSapCodes, setRenamedSapCodes] = useState<Set<number>>(new Set())

  const reassignMutation = useMutation<
    { loaded: number; reassigned: number; skipped: number; deleted: boolean; errors: string[] },
    Error,
    number
  >({
    mutationFn: (sapCode) => apiClient.post(`/articles/groups/reassign-sap/${sapCode}`, {}, { timeout: 300000 }).then((r) => r.data),
    onSuccess: (_data, sapCode) => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setReassignedSapCodes((prev) => new Set(prev).add(sapCode))
    },
  })

  const renameMutation = useMutation<
    { renamed: boolean; oldName: string; newName: string },
    Error,
    number
  >({
    mutationFn: (sapCode) => apiClient.post(`/articles/groups/rename-sap/${sapCode}`).then((r) => r.data),
    onSuccess: (_data, sapCode) => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setRenamedSapCodes((prev) => new Set(prev).add(sapCode))
    },
  })

  // Migration state
  const [showMigration, setShowMigration] = useState(false)
  const [showAllArticles, setShowAllArticles] = useState(false)
  const migrationPreview = useQuery<{
    total: number
    alreadyMigrated: number
    directMapped: number
    keywordMapped: number
    unmapped: number
    suggestions: Array<{
      articleId: string
      itemCode: string
      itemName: string
      currentGroupName: string | null
      currentSapGroupCode: number | null
      suggestedGroupCode: number | null
      suggestedGroupName: string | null
      confidence: 'direct' | 'keyword' | 'none'
    }>
  }>({
    queryKey: ['article-groups-migration-preview', showAllArticles],
    queryFn: () => apiClient.get(`/articles/groups/migration-preview${showAllArticles ? '?includeAll=true' : ''}`).then((r) => r.data),
    enabled: showMigration,
    retry: false,
  })

  const applyMigrationMutation = useMutation<
    { applied: number; errors: string[]; totalSuggested?: number },
    Error,
    { applyAll?: boolean; assignments?: Array<{ articleId: string; newGroupCode: number }> }
  >({
    mutationFn: (body) => apiClient.post('/articles/groups/migration-apply', body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      queryClient.invalidateQueries({ queryKey: ['article-groups-migration-preview'] })
    },
  })

  // Manual assignments for articles without suggestion
  const [manualAssignments, setManualAssignments] = useState<Record<string, number>>({})
  // AI suggestions per article (articleId → {groupCode, groupName, reason})
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, { groupCode: number; groupName: string; reason: string }>>({})
  const [aiLoadingIds, setAiLoadingIds] = useState<Set<string>>(new Set())

  async function fetchAiSuggestion(articleId: string, itemName: string, itemCode?: string) {
    if (aiLoadingIds.has(articleId)) return
    setAiLoadingIds((prev) => new Set(prev).add(articleId))
    try {
      const result = await apiClient.post('/articles/groups/ai-suggest', { articleName: itemName, articleCode: itemCode }).then((r) => r.data) as { groupCode: number; groupName: string; reason: string }
      setAiSuggestions((prev) => ({ ...prev, [articleId]: result }))
      setManualAssignments((prev) => ({ ...prev, [articleId]: result.groupCode }))
    } catch {
      // ignore
    } finally {
      setAiLoadingIds((prev) => { const s = new Set(prev); s.delete(articleId); return s })
    }
  }

  const applyManualMutation = useMutation<
    { applied: number; errors: string[] },
    Error,
    Array<{ articleId: string; newGroupCode: number }>
  >({
    mutationFn: (assignments) => apiClient.post('/articles/groups/migration-apply', { assignments }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      queryClient.invalidateQueries({ queryKey: ['article-groups-migration-preview'] })
      setManualAssignments({})
    },
  })

  // All groups with groupCode for the manual dropdown (including TITEL groups like Schliessanlage)
  const seedGroupOptions = groups
    .filter((g) => g.groupCode)
    .sort((a, b) => (a.groupCode ?? 0) - (b.groupCode ?? 0))

  const createMutation = useMutation({
    mutationFn: (data: { name: string; groupCode: number; isTitle?: boolean }) =>
      apiClient.post('/articles/groups', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setCreatingSection(null)
    },
  })

  // null = not creating; 'root' = new top-level group; sectionKey = new subgroup in that section
  const [creatingSection, setCreatingSection] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState<{ name: string; groupCode: string }>({ name: '', groupCode: '' })

  function startCreate(sectionKey: string, defaultCode?: number) {
    setCreatingSection(sectionKey)
    setCreateForm({ name: '', groupCode: defaultCode != null ? String(defaultCode) : '' })
  }

  function submitCreate(isTitle: boolean) {
    const code = parseInt(createForm.groupCode, 10)
    if (!createForm.name.trim() || isNaN(code)) return
    createMutation.mutate({ name: createForm.name.trim(), groupCode: code, isTitle })
  }

  // ─── Delete group ─────────────────────────────────────────────────────────
  const [deletingGroup, setDeletingGroup] = useState<ArticleGroup | null>(null)
  const [deleteTargetGroupId, setDeleteTargetGroupId] = useState<string>('')
  const [deleteSap, setDeleteSap] = useState(true)

  const groupArticlesQuery = useQuery<Array<{ id: string; itemCode: string; itemName: string }>>({
    queryKey: ['group-articles', deletingGroup?.id],
    queryFn: () => apiClient.get(`/articles/groups/${deletingGroup!.id}/articles`).then((r) => r.data),
    enabled: deletingGroup !== null,
  })

  const reassignDeleteMutation = useMutation<
    { reassigned: number; deletedLocal: boolean; deletedSap: boolean; sapErrors: string[] },
    Error,
    { groupId: string; targetGroupId: string; deleteSap: boolean }
  >({
    mutationFn: ({ groupId, targetGroupId, deleteSap }) =>
      apiClient.post(`/articles/groups/${groupId}/reassign-and-delete`, { targetGroupId, deleteSap }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setDeletingGroup(null)
    },
  })

  const emptyDeleteMutation = useMutation<{ deleted: boolean; reason?: string }, Error, string>({
    mutationFn: (groupId) => apiClient.delete(`/articles/groups/${groupId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setDeletingGroup(null)
    },
  })

  const cascadeDeleteMutation = useMutation<
    { deleted: boolean; reason?: string; deletedChildren: number; sapErrors: string[] },
    Error,
    string
  >({
    mutationFn: (groupId) => apiClient.delete(`/articles/groups/${groupId}/cascade`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      setDeletingGroup(null)
    },
  })

  function openDeleteDialog(group: ArticleGroup) {
    setDeletingGroup(group)
    setDeleteTargetGroupId('')
    setDeleteSap(group.sapGroupCode != null)
    reassignDeleteMutation.reset()
    emptyDeleteMutation.reset()
    cascadeDeleteMutation.reset()
  }

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const toggleSection = (key: string) => setExpandedSections((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<ArticleGroup>>({})

  const startEdit = (group: ArticleGroup) => {
    setEditingId(group.id)
    setEditForm({
      groupCode: group.groupCode ?? undefined,
      name: group.name,
      description: group.description,
      tanssLinkTypeId: group.tanssLinkTypeId,
      tanssTypeId: group.tanssTypeId,
      requiresSerialNumber: group.requiresSerialNumber,
      serialNumberMinPrice: group.serialNumberMinPrice ?? null,
      minStock: group.minStock,
      maxStock: group.maxStock,
      isCustomerProperty: group.isCustomerProperty,
      syncTenantIds: group.syncTenantIds ?? null,
      defaultSapItemType: group.defaultSapItemType ?? null,
      defaultSapUomGroupEntry: group.defaultSapUomGroupEntry ?? null,
      defaultSapInventoryItem: group.defaultSapInventoryItem ?? null,
      defaultSapPurchaseItem: group.defaultSapPurchaseItem ?? null,
      defaultSapSalesItem: group.defaultSapSalesItem ?? null,
    })
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ArticleGroup> }) =>
      apiClient.patch(`/articles/groups/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
  })

  const propagateDefaultsMutation = useMutation<{ updated: number }, Error, string>({
    mutationFn: (id) => apiClient.post(`/articles/groups/${id}/propagate-defaults`, {}).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
  })

  // Optimistic overrides: groupId → syncTenantIds (applied immediately on click, reverted on error)
  const [tenantOverrides, setTenantOverrides] = useState<Record<string, string[] | null>>({})

  // MySQL may return JSON columns as raw strings; parse them to avoid character-spreading
  function parseTenantIds(raw: string[] | string | null | undefined): string[] {
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string') {
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [] } catch { return [] }
    }
    return []
  }
  const [tenantToggleError, setTenantToggleError] = useState<string | null>(null)

  // Single-group toggle (subgroups)
  const tenantToggleMutation = useMutation({
    mutationFn: ({ id, syncTenantIds }: { id: string; syncTenantIds: string[] | null }) =>
      apiClient.patch(`/articles/groups/${id}`, { syncTenantIds }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      // Immediately update the cache so subsequent clicks read the correct value before refetch completes
      queryClient.setQueryData(['article-groups'], (old: ArticleGroup[] | undefined) =>
        old?.map((g) => g.id === vars.id ? { ...g, syncTenantIds: vars.syncTenantIds } : g),
      )
      setTenantOverrides((prev) => { const next = { ...prev }; delete next[vars.id]; return next })
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
    onError: (err, vars) => {
      setTenantOverrides((prev) => { const next = { ...prev }; delete next[vars.id]; return next })
      setTenantToggleError(getErrorMessage(err as Error))
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
  })

  // Cascade toggle for section headers (single request updates header + all children)
  const cascadeTenantMutation = useMutation({
    mutationFn: ({ id, tenantId, active, snapshot: _snap }: { id: string; tenantId: string; active: boolean; snapshot: Record<string, string[] | null> }) =>
      apiClient.post(`/articles/groups/${id}/cascade-tenant`, { tenantId, active }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      // Update cache immediately with the expected values (prevents visual flash before refetch)
      queryClient.setQueryData(['article-groups'], (old: ArticleGroup[] | undefined) =>
        old?.map((g) => g.id in vars.snapshot ? { ...g, syncTenantIds: vars.snapshot[g.id] ?? null } : g),
      )
      setTenantOverrides({})
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
    onError: (err) => {
      setTenantOverrides({})
      setTenantToggleError(getErrorMessage(err as Error))
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
    },
  })

  function handleTenantToggle(group: ArticleGroup, tenantId: string) {
    // Prevent concurrent cascades — second click would race the first in the DB
    if (cascadeTenantMutation.isPending) return
    setTenantToggleError(null)
    // Opt-in: null/[] = no tenant; use override if present (optimistic update)
    const activeIds = parseTenantIds(group.id in tenantOverrides ? tenantOverrides[group.id] : group.syncTenantIds)
    const isActive = activeIds.includes(tenantId)
    const next = isActive
      ? activeIds.filter((id) => id !== tenantId)
      : [...activeIds, tenantId]

    if (isHeader(group)) {
      // Cascade: apply optimistic override for header + all children, then single backend call
      const childGroups = groups.filter(
        (g) => !isHeader(g) && g.groupCode != null && Math.floor(g.groupCode / 100) * 100 === group.groupCode,
      )
      // Snapshot of expected new state for each affected group (used to update cache on success)
      const snapshot: Record<string, string[] | null> = { [group.id]: next.length > 0 ? next : null }
      for (const child of childGroups) {
        const childActive = parseTenantIds(child.id in tenantOverrides ? tenantOverrides[child.id] : child.syncTenantIds)
        const childNext = isActive
          ? childActive.filter((id) => id !== tenantId)
          : childActive.includes(tenantId) ? childActive : [...childActive, tenantId]
        snapshot[child.id] = childNext.length > 0 ? childNext : null
      }
      setTenantOverrides((prev) => ({ ...prev, ...snapshot }))
      cascadeTenantMutation.mutate({ id: group.id, tenantId, active: !isActive, snapshot })
    } else {
      setTenantOverrides((prev) => ({ ...prev, [group.id]: next }))
      tenantToggleMutation.mutate({ id: group.id, syncTenantIds: next })
    }
  }

  // ─── Drag & Drop ─────────────────────────────────────────────────────────────

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const [draggingGroup, setDraggingGroup] = useState<ArticleGroup | null>(null)

  const reorderMutation = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      apiClient.patch(`/articles/groups/${id}`, { sortOrder }).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article-groups'] }),
  })

  function handleDragStart(e: DragStartEvent) {
    const g = groups.find((g) => g.id === e.active.id)
    setDraggingGroup(g ?? null)
  }

  function handleDragEnd(e: DragEndEvent) {
    setDraggingGroup(null)
    const { active, over } = e
    if (!over || active.id === over.id) return
    const draggedGroup = groups.find((g) => g.id === active.id)
    if (!draggedGroup || draggedGroup.isTitle) return

    const overId = String(over.id)
    let targetSectionTitle: ArticleGroup | null = null
    if (overId !== '__uncategorized__') {
      const titleId = overId.replace(/^section-/, '')
      targetSectionTitle = groups.find((g) => g.id === titleId && g.isTitle) ?? null
    }

    const sortedGroups = [...groups].sort((a, b) => a.sortOrder - b.sortOrder)
    let sectionStart: number
    let sectionEnd: number
    if (targetSectionTitle === null) {
      const firstTitle = sortedGroups.find((g) => g.isTitle)
      sectionStart = 0
      sectionEnd = firstTitle ? firstTitle.sortOrder : 999999
    } else {
      sectionStart = targetSectionTitle.sortOrder
      const titleIdx = sortedGroups.findIndex((g) => g.id === targetSectionTitle!.id)
      const nextTitle = sortedGroups.slice(titleIdx + 1).find((g) => g.isTitle)
      sectionEnd = nextTitle ? nextTitle.sortOrder : 999999
    }
    const itemsInSection = sortedGroups.filter(
      (g) => !g.isTitle && g.sortOrder > sectionStart && g.sortOrder < sectionEnd && g.id !== draggedGroup.id,
    )
    const newSortOrder = itemsInSection.length > 0
      ? Math.max(...itemsInSection.map((g) => g.sortOrder)) + 1
      : sectionStart + 1

    reorderMutation.mutate({ id: draggedGroup.id, sortOrder: newSortOrder })
  }

  function DraggableHandle({ groupId, disabled }: { groupId: string; disabled: boolean }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: groupId })
    if (disabled) return <span className="w-4 shrink-0" />
    return (
      <span
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={cn(
          'w-4 shrink-0 flex items-center text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing transition-colors',
          isDragging && 'opacity-0',
        )}
        onClick={(e) => e.stopPropagation()}
        title="Verschieben"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </span>
    )
  }

  function DroppableSection({ id, children, className }: { id: string; children?: ReactNode; className?: string }) {
    const { setNodeRef, isOver } = useDroppable({ id })
    return (
      <div ref={setNodeRef} className={cn(className, isOver && draggingGroup && 'ring-2 ring-brand-400 ring-inset bg-brand-50/30')}>
        {children}
      </div>
    )
  }

  // ─── Tenant helper: clickable toggle chips (opt-in: empty = no tenant) ────────
  function TenantToggleChips({ group }: { group: ArticleGroup }) {
    if (sapTenants.length === 0) return null
    // Opt-in: null/undefined = no tenants; use override for immediate UI response
    const activeIds = parseTenantIds(group.id in tenantOverrides ? tenantOverrides[group.id] : group.syncTenantIds)
    // Disable during any pending cascade (prevents race condition with concurrent DB reads)
    const isPending = (tenantToggleMutation.isPending && tenantToggleMutation.variables?.id === group.id)
      || cascadeTenantMutation.isPending
    return (
      <div className="flex gap-1 items-center" onClick={(e) => e.stopPropagation()}>
        {activeIds.length === 0 && (
          <span className="text-[10px] text-gray-400 italic mr-1">kein Mandant</span>
        )}
        {sapTenants.map((t) => {
          const isActive = activeIds.includes(t.id)
          return (
            <button
              key={t.id}
              onClick={() => handleTenantToggle(group, t.id)}
              disabled={isPending}
              title={isActive ? `${t.code} aus Sync entfernen` : `${t.code} zu Sync hinzufügen`}
              className={cn(
                'text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5 transition-colors',
                isActive
                  ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
                isPending && 'opacity-50 cursor-not-allowed',
              )}
            >
              <Building2 className="w-2.5 h-2.5" />{t.code}
            </button>
          )
        })}
      </div>
    )
  }

  // ─── Tree hierarchy ───────────────────────────────────────────────────────
  // A group is a section header if:
  //   (a) it has isTitle=true (explicit), OR
  //   (b) its groupCode is a multiple of 100 (e.g. 1000, 1100, 1500, ...)
  // Children: groupCode not a multiple of 100, parent = floor(groupCode/100)*100
  const isHeader = (g: ArticleGroup) =>
    g.isTitle || (g.groupCode != null && g.groupCode % 100 === 0)

  const withCode = groups.filter((g) => g.groupCode != null)
  const withoutCode = groups.filter((g) => g.groupCode == null)

  const headerGroups = withCode
    .filter(isHeader)
    .sort((a, b) => (a.groupCode ?? a.sortOrder) - (b.groupCode ?? b.sortOrder))

  const headerCodeSet = new Set(headerGroups.map((g) => g.groupCode!))

  const sections: { title: ArticleGroup | null; items: ArticleGroup[] }[] = headerGroups.map(
    (parent) => ({
      title: parent,
      items: withCode
        .filter((g) => {
          if (isHeader(g)) return false
          return Math.floor(g.groupCode! / 100) * 100 === parent.groupCode
        })
        .sort((a, b) => a.groupCode! - b.groupCode!),
    }),
  )

  // Orphaned (has groupCode, but no matching header) + SAP-only (no groupCode)
  const ungrouped = [
    ...withCode.filter((g) => {
      if (isHeader(g)) return false
      return !headerCodeSet.has(Math.floor(g.groupCode! / 100) * 100)
    }),
    ...withoutCode,
  ].sort((a, b) => (a.sapGroupCode ?? a.sortOrder) - (b.sapGroupCode ?? b.sortOrder))

  if (ungrouped.length > 0) {
    sections.push({ title: null, items: ungrouped })
  }

  const nonTitleGroups = groups.filter((g) => !isHeader(g))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {isLoading ? 'Lade Warengruppen (SAP)...' : `${nonTitleGroups.length} Warengruppen in ${headerGroups.length} Sektionen`}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => startCreate('root', Math.ceil(((headerGroups.at(-1)?.groupCode ?? 900) + 100) / 100) * 100)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <Plus className="w-4 h-4" />Neue Obergruppe
          </button>
          <button
            onClick={() => runSapSync()}
            disabled={sapSyncPhase.status === 'running' || groups.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {sapSyncPhase.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Nach SAP synchronisieren
          </button>
          <button
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
            title="Gruppen-Namen auf Standard-Bezeichnungen zurücksetzen"
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 border border-gray-300"
          >
            {seedMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Namen reparieren
          </button>
        </div>
      </div>

      {/* Inline form: Neue Obergruppe */}
      {creatingSection === 'root' && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg">
            <Layers className="w-4 h-4 text-brand-500 shrink-0" />
            <input
              type="number"
              autoFocus
              value={createForm.groupCode}
              onChange={(e) => setCreateForm((f) => ({ ...f, groupCode: e.target.value }))}
              placeholder="Code (z.B. 2000)"
              className="w-28 text-sm border border-gray-300 rounded px-2 py-1 font-mono"
            />
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(true); if (e.key === 'Escape') setCreatingSection(null) }}
              placeholder="Name der Obergruppe"
              className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
            />
            <button onClick={() => submitCreate(true)} disabled={createMutation.isPending || !createForm.name || !createForm.groupCode} className="flex items-center gap-1 px-2 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50">
              {createMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Erstellen
            </button>
            <button onClick={() => setCreatingSection(null)} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"><X className="w-3 h-3" /></button>
          </div>
          {createMutation.isError && creatingSection === 'root' && (
            <p className="px-3 text-xs text-red-600 font-mono">{getErrorMessage(createMutation.error as Error)}</p>
          )}
        </div>
      )}
      {/* SAP Sync: Live progress indicator */}
      {sapSyncPhase.status === 'running' && (
        <div className="px-3 py-2.5 text-sm bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1.5">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin shrink-0" />
            <span className="text-blue-800 font-medium">SAP-Synchronisierung läuft…</span>
          </div>
          <div className="pl-6 space-y-0.5">
            <p className="text-blue-700 text-xs">
              Mandant {sapSyncPhase.tenantIdx + 1}/{sapSyncPhase.tenantCount}:
              <span className="font-medium ml-1">{sapSyncPhase.tenantCode}</span>
            </p>
            <div className="flex items-center gap-1.5 text-xs text-blue-600">
              {(['sync', 'cleanup', 'compare'] as const).map((s) => (
                <span
                  key={s}
                  className={cn(
                    'px-1.5 py-0.5 rounded',
                    s === sapSyncPhase.step ? 'bg-blue-600 text-white font-medium' : 'bg-blue-100 text-blue-400',
                  )}
                >
                  {SAP_SYNC_STEP_LABELS[s]}
                </span>
              ))}
            </div>
            {sapSyncPhase.step === 'sync' && sapSyncPhase.syncProgress && (
              <p className="text-blue-600 text-xs">
                {sapSyncPhase.syncProgress.processed}/{sapSyncPhase.syncProgress.total} Gruppen verarbeitet…
              </p>
            )}
          </div>
        </div>
      )}

      {sapSyncPhase.status === 'error' && (
        <div className="px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg space-y-1">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            <span className="text-red-800 font-medium">SAP-Synchronisierung fehlgeschlagen</span>
            <button
              onClick={() => setSapSyncPhase({ status: 'idle' })}
              className="ml-auto text-xs text-red-600 underline hover:text-red-800"
            >
              Zurücksetzen
            </button>
          </div>
          <p className="text-red-700 text-xs font-mono break-all pl-6">{sapSyncPhase.message}</p>
        </div>
      )}

      {/* SAP Sync Result: Summary + Comparison Table */}
      {sapSyncPhase.status === 'done' && (() => {
        const data = sapSyncPhase.result
        return (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">SAP-Synchronisierung abgeschlossen</span>
          </div>

          {/* Action summary */}
          <div className="flex flex-wrap gap-3 text-xs text-green-700">
            {data.sync.synced > 0 && <span>{data.sync.synced} neu angelegt</span>}
            {data.sync.renamed > 0 && <span>{data.sync.renamed} umbenannt</span>}
            {data.cleanup.deleted > 0 && <span>{data.cleanup.deleted} verwaiste gelöscht</span>}
            {data.cleanup.reassigned > 0 && <span>{data.cleanup.reassigned} Artikel verschoben</span>}
            {data.cleanup.failed > 0 && <span className="text-amber-700">{data.cleanup.failed} nicht löschbar</span>}
            {data.errors.length > 0 && <span className="text-red-600 font-medium">{data.errors.length} Fehler</span>}
            {data.sync.synced === 0 && data.sync.renamed === 0 && data.cleanup.deleted === 0 && data.cleanup.reassigned === 0 && data.errors.length === 0 && (
              <span>Keine Änderungen nötig</span>
            )}
          </div>

          {/* Per-tenant results */}
          {data.perTenant?.length > 0 && (
            <div className="space-y-1">
              {data.perTenant.map((t) => (
                <div key={t.tenantId} className={`text-xs rounded px-2 py-1.5 ${t.errors.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center gap-2 font-medium">
                    <span className={t.errors.length > 0 ? 'text-red-700' : 'text-gray-700'}>{t.tenantCode}</span>
                    {t.errors.length === 0 ? (
                      <span className="text-green-600">{t.sync.synced} angelegt · {t.sync.renamed} umbenannt · {t.sync.skipped} übersprungen</span>
                    ) : (
                      <span className="text-red-600">{t.errors.length} Fehler</span>
                    )}
                  </div>
                  {t.errors.length > 0 && (
                    <ul className="mt-1 space-y-0.5 pl-2">
                      {t.errors.map((e, i) => (
                        <li key={i} className="text-red-700 font-mono break-all">{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Cleanup details (collapsible) */}
          {data.cleanup.details.length > 0 && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer hover:text-gray-800">Bereinigung: {data.cleanup.details.length} Aktionen</summary>
              <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto pl-2">
                {data.cleanup.details.map((d, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-gray-400 font-mono">#{d.code}</span>
                    <span>{d.name}</span>
                    <span className={d.action.startsWith('Nicht') || d.action.startsWith('Fehler') ? 'text-red-500' : 'text-green-600'}>{d.action}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* SAP Comparison Summary */}
          {(() => {
            const raw = data.comparison.summary
            const doneCount = reassignedSapCodes.size
            const renameCount = renamedSapCodes.size
            const s = {
              ...raw,
              orphaned: Math.max(0, raw.orphaned - doneCount),
              nameMismatch: Math.max(0, raw.nameMismatch - renameCount),
              ok: raw.ok + doneCount + renameCount,
              total: raw.total - doneCount,
            }
            const hasIssues = s.nameMismatch > 0 || s.orphaned > 0 || s.missingInSap > 0
            return (
              <div className="space-y-2">
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2 border border-gray-200">
                    <div className="text-lg font-bold text-gray-800">{s.total}</div>
                    <div className="text-[10px] text-gray-500">SAP-Gruppen</div>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-green-200">
                    <div className="text-lg font-bold text-green-700">{s.ok}</div>
                    <div className="text-[10px] text-green-600">OK</div>
                  </div>
                  <div className={`bg-white rounded-lg p-2 border ${s.nameMismatch > 0 ? 'border-amber-300' : 'border-gray-200'}`}>
                    <div className={`text-lg font-bold ${s.nameMismatch > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{s.nameMismatch}</div>
                    <div className="text-[10px] text-gray-500">Name falsch</div>
                  </div>
                  <div className={`bg-white rounded-lg p-2 border ${s.orphaned > 0 ? 'border-red-300' : 'border-gray-200'}`}>
                    <div className={`text-lg font-bold ${s.orphaned > 0 ? 'text-red-700' : 'text-gray-400'}`}>{s.orphaned}</div>
                    <div className="text-[10px] text-gray-500">Verwaist</div>
                  </div>
                  <div className={`bg-white rounded-lg p-2 border ${s.missingInSap > 0 ? 'border-purple-300' : 'border-gray-200'}`}>
                    <div className={`text-lg font-bold ${s.missingInSap > 0 ? 'text-purple-700' : 'text-gray-400'}`}>{s.missingInSap}</div>
                    <div className="text-[10px] text-gray-500">Fehlt in SAP</div>
                  </div>
                </div>

                {!hasIssues && (
                  <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Alle SAP-Gruppen sind korrekt synchronisiert.</p>
                )}

                {/* Only show table if there are issues */}
                {hasIssues && (
                  <details className="text-xs" open>
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                      {s.nameMismatch + s.orphaned + s.missingInSap} Probleme anzeigen
                    </summary>
                    <div className="mt-2 max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
                      {(() => {
                        const rows: SapGroupEntry[] = [
                          ...data.comparison.sapGroups.filter(
                            (e) => e.status !== 'ok' && !reassignedSapCodes.has(e.sapCode) && !renamedSapCodes.has(e.sapCode),
                          ),
                          ...data.comparison.missingInSap,
                        ]
                        const columns: ColumnDef<SapGroupEntry, unknown>[] = [
                          {
                            accessorKey: 'sapCode',
                            header: 'SAP #',
                            size: 80,
                            cell: ({ row }) => (
                              <span className="font-mono text-gray-500">
                                {row.original.status === 'missing_in_sap' ? '—' : row.original.sapCode}
                              </span>
                            ),
                          },
                          {
                            accessorKey: 'sapName',
                            header: 'SAP-Name',
                            cell: ({ row }) =>
                              row.original.status === 'missing_in_sap' ? (
                                <span className="text-gray-400 italic">{row.original.expectedSapName}</span>
                              ) : (
                                <div className="text-gray-900">
                                  {row.original.sapName}
                                  {row.original.expectedSapName && (
                                    <div className="text-green-600 text-[10px]">Soll: {row.original.expectedSapName}</div>
                                  )}
                                </div>
                              ),
                          },
                          {
                            accessorKey: 'localGroupCode',
                            header: 'Code',
                            size: 80,
                            cell: ({ row }) => (
                              <span className="font-mono text-gray-400">{row.original.localGroupCode ?? '—'}</span>
                            ),
                          },
                          {
                            accessorKey: 'localName',
                            header: 'Lokaler Name',
                            cell: ({ row }) => (
                              <span className="text-gray-600">
                                {row.original.localName ?? <span className="text-red-400 italic">nicht verknüpft</span>}
                              </span>
                            ),
                          },
                          {
                            id: '__action__',
                            header: 'Status',
                            size: 100,
                            enableSorting: false,
                            cell: ({ row }) => {
                              const e = row.original
                              if (e.status === 'name_mismatch') {
                                return (
                                  <button
                                    onClick={() => renameMutation.mutate(e.sapCode)}
                                    disabled={renameMutation.isPending}
                                    className="px-2 py-0.5 bg-amber-600 text-white rounded text-[10px] font-medium hover:bg-amber-700 disabled:opacity-50"
                                  >
                                    {renameMutation.isPending && renameMutation.variables === e.sapCode ? 'Läuft...' : 'Umbenennen'}
                                  </button>
                                )
                              }
                              if (e.status === 'orphaned') {
                                return (
                                  <button
                                    onClick={() => reassignMutation.mutate(e.sapCode)}
                                    disabled={reassignMutation.isPending}
                                    className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-medium hover:bg-red-700 disabled:opacity-50"
                                  >
                                    {reassignMutation.isPending && reassignMutation.variables === e.sapCode ? 'Läuft...' : 'Umziehen'}
                                  </button>
                                )
                              }
                              if (e.status === 'missing_in_sap') {
                                return (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-medium">Fehlt</span>
                                )
                              }
                              return null
                            },
                          },
                        ]
                        return (
                          <DataTable<SapGroupEntry>
                            data={rows}
                            columns={columns}
                            getRowId={(e) => (e.status === 'missing_in_sap' ? `local-${e.localGroupId}` : `sap-${e.sapCode}`)}
                            enableSorting={false}
                            emptyState={<EmptyState size="sm" title="Keine Probleme" />}
                          />
                        )
                      })()}
                    </div>
                  </details>
                )}
              </div>
            )
          })()}
        </div>
        )
      })()}

      {reassignMutation.isSuccess && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-green-800">
            SAP #{reassignMutation.variables}: {reassignMutation.data.reassigned} Artikel verschoben
            {reassignMutation.data.skipped > 0 && `, ${reassignMutation.data.skipped} ohne Zuordnung`}
            {reassignMutation.data.deleted ? ', Gruppe gelöscht' : ', Gruppe konnte nicht gelöscht werden'}
          </span>
        </div>
      )}
      {reassignMutation.isError && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-red-800">Umzug fehlgeschlagen: {getErrorMessage(reassignMutation.error)}</span>
        </div>
      )}
      {renameMutation.isSuccess && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-green-800">
            SAP #{renameMutation.variables}: &quot;{renameMutation.data.oldName}&quot; → &quot;{renameMutation.data.newName}&quot;
          </span>
        </div>
      )}
      {renameMutation.isError && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-red-800">Umbenennung fehlgeschlagen: {getErrorMessage(renameMutation.error)}</span>
        </div>
      )}

      {/* Migration Panel */}
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-amber-900">Artikel-Migration</h3>
            <p className="text-xs text-amber-700 mt-0.5">Artikel aus alten SAP-Gruppen den neuen Gruppen zuordnen</p>
          </div>
          <button
            onClick={() => setShowMigration(!showMigration)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            {showMigration ? 'Ausblenden' : 'Vorschau anzeigen'}
          </button>
        </div>

        {showMigration && migrationPreview.isLoading && (
          <div className="flex items-center gap-2 py-4 justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            <span className="text-sm text-amber-700">Analysiere {groups.length > 0 ? '~1800' : ''} Artikel...</span>
          </div>
        )}

        {showMigration && migrationPreview.isError && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-red-800">Vorschau fehlgeschlagen</span>
          </div>
        )}

        {showMigration && migrationPreview.data && (
          <div className="space-y-3">
            {/* Summary */}
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="bg-white rounded-lg p-2 border border-gray-200">
                <div className="text-lg font-bold text-gray-500">{migrationPreview.data.alreadyMigrated}</div>
                <div className="text-xs text-gray-500">Bereits migriert</div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-amber-100">
                <div className="text-lg font-bold text-gray-900">{migrationPreview.data.total}</div>
                <div className="text-xs text-gray-500">{showAllArticles ? 'In Tabelle' : 'Noch offen'}</div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-green-200">
                <div className="text-lg font-bold text-green-700">{migrationPreview.data.directMapped}</div>
                <div className="text-xs text-gray-500">1:1 Mapping</div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-blue-200">
                <div className="text-lg font-bold text-blue-700">{migrationPreview.data.keywordMapped}</div>
                <div className="text-xs text-gray-500">Keyword-Match</div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-red-200">
                <div className="text-lg font-bold text-red-700">{migrationPreview.data.unmapped}</div>
                <div className="text-xs text-gray-500">Kein Vorschlag</div>
              </div>
            </div>

            {/* Toggle: show all articles including already migrated */}
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllArticles}
                onChange={(e) => setShowAllArticles(e.target.checked)}
              />
              Bereits migrierte Artikel auch anzeigen (zur Überprüfung)
            </label>

            {/* Apply buttons */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => applyMigrationMutation.mutate({ applyAll: true })}
                disabled={applyMigrationMutation.isPending || (migrationPreview.data.directMapped + migrationPreview.data.keywordMapped) === 0}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {applyMigrationMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Alle Vorschläge anwenden ({migrationPreview.data.directMapped + migrationPreview.data.keywordMapped} Artikel)
              </button>
              {migrationPreview.data.suggestions.some(s => s.confidence === 'none') && (
                <button
                  onClick={() => {
                    const noSuggestion = migrationPreview.data!.suggestions.filter(s => s.confidence === 'none')
                    noSuggestion.forEach(s => fetchAiSuggestion(s.articleId, s.itemName, s.itemCode))
                  }}
                  disabled={aiLoadingIds.size > 0}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  {aiLoadingIds.size > 0
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> {aiLoadingIds.size} werden verarbeitet...</>
                    : <><Sparkles className="w-4 h-4" /> KI-Vorschläge für alle ({migrationPreview.data.suggestions.filter(s => s.confidence === 'none').length})</>
                  }
                </button>
              )}
            </div>

            {applyMigrationMutation.isSuccess && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-green-800">
                  {applyMigrationMutation.data.applied} Artikel umgesetzt
                  {applyMigrationMutation.data.errors.length > 0 && `, ${applyMigrationMutation.data.errors.length} Fehler`}
                </span>
              </div>
            )}

            {applyMigrationMutation.isError && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-red-800">Migration fehlgeschlagen</span>
              </div>
            )}

            {/* Manual apply button */}
            {Object.keys(manualAssignments).length > 0 && (
              <button
                onClick={() => {
                  const assignments = Object.entries(manualAssignments).map(([articleId, newGroupCode]) => ({ articleId, newGroupCode }))
                  applyManualMutation.mutate(assignments)
                }}
                disabled={applyManualMutation.isPending}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {applyManualMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {Object.keys(manualAssignments).length} manuelle Zuweisungen anwenden
              </button>
            )}

            {applyManualMutation.isSuccess && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-green-800">{applyManualMutation.data.applied} Artikel manuell zugewiesen</span>
              </div>
            )}

            {/* Suggestion table */}
            {migrationPreview.data.suggestions.length > 0 && (
              <div className="max-h-[500px] overflow-y-auto border border-gray-200 rounded-lg">
                {(() => {
                  type Suggestion = (typeof migrationPreview.data.suggestions)[number]
                  const rows = migrationPreview.data.suggestions.slice(0, 200)
                  const columns: ColumnDef<Suggestion, unknown>[] = [
                    {
                      accessorKey: 'itemCode',
                      header: 'Artikel',
                      cell: ({ row }) => (
                        <div>
                          <div className="font-mono text-gray-400">{row.original.itemCode}</div>
                          <div className="text-gray-900 truncate max-w-xs">{row.original.itemName}</div>
                        </div>
                      ),
                    },
                    {
                      accessorKey: 'currentGroupName',
                      header: 'Aktuelle Gruppe',
                      cell: ({ row }) => (
                        <span className="text-gray-600">
                          {row.original.currentGroupName ?? '—'}
                          {row.original.currentSapGroupCode != null && (
                            <span className="text-gray-400 ml-1">#{row.original.currentSapGroupCode}</span>
                          )}
                        </span>
                      ),
                    },
                    {
                      accessorKey: 'suggestedGroupName',
                      header: 'Neue Gruppe',
                      size: 220,
                      cell: ({ row }) => {
                        const s = row.original
                        if (s.confidence !== 'none') {
                          return (
                            <span className="text-green-700 font-medium">
                              {s.suggestedGroupCode} – {s.suggestedGroupName}
                            </span>
                          )
                        }
                        return (
                          <div className="flex gap-1 items-start">
                            <div className="flex-1 min-w-0">
                              <GroupSearchSelect
                                options={seedGroupOptions as Array<{ id: string; groupCode: number; name: string }>}
                                value={manualAssignments[s.articleId] ?? ''}
                                onChange={(code) => {
                                  setManualAssignments((prev) => {
                                    if (code === '') {
                                      const next = { ...prev }; delete next[s.articleId]; return next
                                    }
                                    return { ...prev, [s.articleId]: code as number }
                                  })
                                }}
                              />
                              {aiSuggestions[s.articleId] && (
                                <p className="text-[10px] text-amber-600 mt-0.5 truncate" title={aiSuggestions[s.articleId].reason}>
                                  KI: {aiSuggestions[s.articleId].reason}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => fetchAiSuggestion(s.articleId, s.itemName, s.itemCode)}
                              disabled={aiLoadingIds.has(s.articleId)}
                              title="KI-Vorschlag anfordern"
                              className="shrink-0 p-1 rounded text-amber-500 hover:bg-amber-50 disabled:opacity-50"
                            >
                              {aiLoadingIds.has(s.articleId)
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Sparkles className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        )
                      },
                    },
                    {
                      accessorKey: 'confidence',
                      header: 'Typ',
                      size: 80,
                      enableSorting: false,
                      cell: ({ row }) => {
                        const s = row.original
                        if (s.confidence === 'direct') return <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">1:1</span>
                        if (s.confidence === 'keyword') return <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Keyword</span>
                        if (!manualAssignments[s.articleId]) return <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">Manuell</span>
                        return <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">Gewählt</span>
                      },
                    },
                  ]
                  return (
                    <DataTable<Suggestion>
                      data={rows}
                      columns={columns}
                      getRowId={(s) => s.articleId}
                      enableSorting={false}
                      emptyState={<EmptyState size="sm" title="Keine Vorschläge" />}
                    />
                  )
                })()}
                {migrationPreview.data.suggestions.length > 200 && (
                  <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                    ... und {migrationPreview.data.suggestions.length - 200} weitere Artikel
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {tenantToggleError && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-red-700">Mandant konnte nicht gespeichert werden: {tenantToggleError}</span>
          <button onClick={() => setTenantToggleError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      )}

      {groupsIsError && (
        <ErrorState
          title="Warengruppen konnten nicht geladen werden"
          details={groupsError ? JSON.stringify(groupsError, Object.getOwnPropertyNames(groupsError as object), 2) : undefined}
          onRetry={() => queryClient.invalidateQueries({ queryKey: ['article-groups'] })}
        />
      )}

      {!isLoading && !groupsIsError && groups.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-400">
          Keine Warengruppen (SAP) vorhanden. Klicke &quot;Standardgruppen anlegen&quot; um die Grundstruktur zu erstellen.
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
        {sections.map((section, sIdx) => {
          const sectionKey = section.title?.id ?? `uncategorized-${sIdx}`
          const dropId = section.title ? `section-${section.title.id}` : '__uncategorized__'
          const isCollapsed = !expandedSections.has(sectionKey)
          const allItems = section.title ? [section.title, ...section.items] : section.items
          const totalArticles = allItems.reduce((s, g) => s + (g.articleCount ?? 0), 0)

          return (
            <div key={sectionKey}>
              {/* Section header row */}
              <div
                className="flex items-center bg-gray-50 hover:bg-gray-100 transition-colors select-none"
              >
                {/* Collapse toggle */}
                <button
                  className="flex items-center justify-center w-8 h-full py-3 text-gray-400 hover:text-gray-600 shrink-0"
                  onClick={() => toggleSection(sectionKey)}
                  title={isCollapsed ? 'Aufklappen' : 'Zuklappen'}
                >
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {/* Editable title row */}
                {section.title && editingId === section.title.id ? (
                  <div className="flex-1 px-2 py-2 space-y-2">
                    <div className="flex gap-2 items-center">
                      <input type="number" className="w-24 text-sm border border-gray-300 rounded px-2 py-1 font-mono" title="Code (Sortierung + SAP-ID)" value={editForm.groupCode ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, groupCode: e.target.value ? Number(e.target.value) : undefined }))} placeholder="Code" />
                      <input className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" value={editForm.name ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" />
                      <input className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" value={editForm.description ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} placeholder="Beschreibung" />
                    </div>
                    <div className="flex flex-wrap gap-3 items-center text-xs text-gray-600">
                      <label className="flex items-center gap-1">TANSS Typ:
                        <select className="border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.tanssLinkTypeId ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, tanssLinkTypeId: e.target.value ? Number(e.target.value) : undefined }))}>
                          <option value="">—</option>
                          {Object.entries(TANSS_LINK_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </label>
                      <label className="flex items-center gap-1">Periph.-ID: <input type="number" className="w-14 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.tanssTypeId ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, tanssTypeId: e.target.value ? Number(e.target.value) : undefined }))} /></label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={editForm.requiresSerialNumber ?? false} onChange={(e) => setEditForm((f) => ({ ...f, requiresSerialNumber: e.target.checked }))} /><span>SN</span></label>
                      <label className="flex items-center gap-1"><span className="text-gray-600">SN ab €</span><input type="number" min="0" step="0.01" className="w-20 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.serialNumberMinPrice ?? ''} placeholder="—" onChange={(e) => setEditForm((f) => ({ ...f, serialNumberMinPrice: e.target.value ? Number(e.target.value) : null }))} /></label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={editForm.isCustomerProperty ?? false} onChange={(e) => setEditForm((f) => ({ ...f, isCustomerProperty: e.target.checked }))} /><span>Kd-Ware</span></label>
                      <label className="flex items-center gap-1">
                        <span className="flex items-center gap-0.5 text-gray-600">
                          Min:
                          <Tooltip content="Mindestbestand-Schwellwert für die Warengruppe. Noch nicht aktiv verknüpft – für zukünftige Nutzung vorgesehen." side="top">
                            <Info className="w-3 h-3 text-gray-400 cursor-default" />
                          </Tooltip>
                        </span>
                        <input type="number" className="w-12 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.minStock ?? 0} onChange={(e) => setEditForm((f) => ({ ...f, minStock: Number(e.target.value) }))} />
                      </label>
                      <label className="flex items-center gap-1">
                        <span className="flex items-center gap-0.5 text-gray-600">
                          Max:
                          <Tooltip content="Maximalbestand-Schwellwert für die Warengruppe. Noch nicht aktiv verknüpft – für zukünftige Nutzung vorgesehen." side="top">
                            <Info className="w-3 h-3 text-gray-400 cursor-default" />
                          </Tooltip>
                        </span>
                        <input type="number" className="w-12 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.maxStock ?? 0} onChange={(e) => setEditForm((f) => ({ ...f, maxStock: Number(e.target.value) }))} />
                      </label>
                    </div>
                    <SapDefaultsEditRow editForm={editForm} setEditForm={setEditForm} uomGroupOptions={uomGroupOptions} />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => updateMutation.mutate({ id: section.title!.id, data: editForm })} disabled={updateMutation.isPending} className="flex items-center gap-1 px-2 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700"><Check className="w-3 h-3" /> Speichern</button>
                      {section.items.length > 0 && (
                        <button
                          onClick={() => propagateDefaultsMutation.mutate(section.title!.id)}
                          disabled={propagateDefaultsMutation.isPending}
                          title={`SAP-Defaults auf ${section.items.length} Untergruppe${section.items.length !== 1 ? 'n' : ''} übertragen`}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100 disabled:opacity-50"
                        >
                          {propagateDefaultsMutation.isPending
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <ChevronsDown className="w-3 h-3" />}
                          Auf Subgruppen übertragen
                        </button>
                      )}
                      {propagateDefaultsMutation.isSuccess && propagateDefaultsMutation.data && (
                        <span className="flex items-center gap-1 px-2 py-1 text-xs text-green-700">
                          <CheckCircle2 className="w-3 h-3" /> {propagateDefaultsMutation.data.updated} übertragen
                        </span>
                      )}
                      <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"><X className="w-3 h-3" /> Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex-1 flex items-center justify-between py-2.5 pr-4 cursor-pointer"
                    onClick={() => section.title && startEdit(section.title)}
                  >
                    <div className="flex items-center gap-3">
                      <Layers className="w-4 h-4 text-brand-500 shrink-0" />
                      {section.title ? (
                        <>
                          <span className="text-xs text-gray-400 font-mono w-10 shrink-0">{section.title.groupCode}</span>
                          <span className="text-sm font-semibold text-gray-900">{section.title.name}</span>
                        </>
                      ) : (
                        <span className="text-sm font-semibold text-gray-500 italic">Sonstige / ohne Struktur</span>
                      )}
                      {section.title?.tanssLinkTypeId && (
                        <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', TANSS_LINK_TYPE_COLORS[section.title.tanssLinkTypeId])}>
                          {TANSS_LINK_TYPE_LABELS[section.title.tanssLinkTypeId]}
                        </span>
                      )}
                      {section.title && <TenantToggleChips group={section.title} />}
                      {section.title?.requiresSerialNumber && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">SN</span>}
                      {section.title && !section.title.requiresSerialNumber && section.title.serialNumberMinPrice != null && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-600 font-medium">SN ab {section.title.serialNumberMinPrice.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400" onClick={(e) => e.stopPropagation()}>
                      <span>{totalArticles} Art.</span>
                      {section.title && (tenantSapMap[section.title.id]?.length ? (
                        <span className="font-mono text-gray-300">
                          {tenantSapMap[section.title.id].map(t => `${t.tenantCode} #${t.sapCode}`).join(' · ')}
                        </span>
                      ) : section.title.sapGroupCode != null ? (
                        <span className="font-mono">SAP #{section.title.sapGroupCode}</span>
                      ) : null)}
                      <span className="text-gray-300">|</span>
                      <span>{section.items.length} Untergruppen</span>
                      {section.title && (
                        <button
                          onClick={() => {
                            const lastChild = section.items.at(-1)
                            const parentCode = section.title!.groupCode ?? 0
                            const nextCode = lastChild?.groupCode != null
                              ? lastChild.groupCode + 1
                              : parentCode + 10
                            setExpandedSections((prev) => { const s = new Set(prev); s.add(sectionKey); return s })
                            startCreate(sectionKey, nextCode)
                          }}
                          className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-brand-600 hover:bg-brand-50 font-medium"
                          title="Neue Untergruppe"
                        >
                          <Plus className="w-3 h-3" /> Untergruppe
                        </button>
                      )}
                      {section.title && (
                        <button
                          onClick={() => openDeleteDialog(section.title!)}
                          className="p-0.5 text-gray-300 hover:text-red-500 transition-colors"
                          title="Gruppe löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Children */}
              {!isCollapsed && section.items.length > 0 && (
                <DroppableSection id={dropId} className="divide-y divide-gray-100">
                  {section.items.map((g) => (
                    <div key={g.id} className={cn('pl-4 pr-4 py-2 hover:bg-gray-50 transition-colors border-l-2 border-l-brand-100', draggingGroup?.id === g.id && 'opacity-40')}>
                      {editingId === g.id ? (
                        <div className="space-y-2 py-1">
                          <div className="flex gap-2 items-center">
                            <input type="number" className="w-24 text-sm border border-gray-300 rounded px-2 py-1 font-mono" title="Code (Sortierung + SAP-ID)" value={editForm.groupCode ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, groupCode: e.target.value ? Number(e.target.value) : undefined }))} placeholder="Code" />
                            <input className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" value={editForm.name ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" />
                            <input className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" value={editForm.description ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} placeholder="Beschreibung" />
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs">
                            <label className="flex items-center gap-1">
                              <span className="text-gray-600">TANSS Typ:</span>
                              <select className="border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.tanssLinkTypeId ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, tanssLinkTypeId: e.target.value ? Number(e.target.value) : undefined }))}>
                                <option value="">—</option>
                                <option value="1">PC</option><option value="2">Server</option><option value="3">Peripherie</option><option value="4">Komponente</option>
                              </select>
                            </label>
                            <label className="flex items-center gap-1">
                              <span className="text-gray-600">Periph.-ID:</span>
                              <input type="number" className="w-14 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.tanssTypeId ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, tanssTypeId: e.target.value ? Number(e.target.value) : undefined }))} />
                            </label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={editForm.requiresSerialNumber ?? false} onChange={(e) => setEditForm((f) => ({ ...f, requiresSerialNumber: e.target.checked }))} /><span className="text-gray-600">SN</span></label>
                            <label className="flex items-center gap-1"><span className="text-gray-600">SN ab €</span><input type="number" min="0" step="0.01" className="w-20 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.serialNumberMinPrice ?? ''} placeholder="—" onChange={(e) => setEditForm((f) => ({ ...f, serialNumberMinPrice: e.target.value ? Number(e.target.value) : null }))} /></label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={editForm.isCustomerProperty ?? false} onChange={(e) => setEditForm((f) => ({ ...f, isCustomerProperty: e.target.checked }))} /><span className="text-gray-600">Kd-Ware</span></label>
                            <label className="flex items-center gap-1">
                              <span className="flex items-center gap-0.5 text-gray-600">
                                Min:
                                <Tooltip content="Mindestbestand-Schwellwert für die Warengruppe. Noch nicht aktiv verknüpft – für zukünftige Nutzung vorgesehen." side="top">
                                  <Info className="w-3 h-3 text-gray-400 cursor-default" />
                                </Tooltip>
                              </span>
                              <input type="number" className="w-12 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.minStock ?? 0} onChange={(e) => setEditForm((f) => ({ ...f, minStock: Number(e.target.value) }))} />
                            </label>
                            <label className="flex items-center gap-1">
                              <span className="flex items-center gap-0.5 text-gray-600">
                                Max:
                                <Tooltip content="Maximalbestand-Schwellwert für die Warengruppe. Noch nicht aktiv verknüpft – für zukünftige Nutzung vorgesehen." side="top">
                                  <Info className="w-3 h-3 text-gray-400 cursor-default" />
                                </Tooltip>
                              </span>
                              <input type="number" className="w-12 border border-gray-300 rounded px-1 py-0.5 text-xs" value={editForm.maxStock ?? 0} onChange={(e) => setEditForm((f) => ({ ...f, maxStock: Number(e.target.value) }))} />
                            </label>
                          </div>
                          <SapDefaultsEditRow editForm={editForm} setEditForm={setEditForm} uomGroupOptions={uomGroupOptions} />
                          <div className="flex gap-2">
                            <button onClick={() => updateMutation.mutate({ id: g.id, data: editForm })} disabled={updateMutation.isPending} className="flex items-center gap-1 px-2 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700"><Check className="w-3 h-3" /> Speichern</button>
                            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"><X className="w-3 h-3" /> Abbrechen</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between cursor-pointer" onClick={() => startEdit(g)}>
                          <div className="flex items-center gap-3">
                            <DraggableHandle groupId={g.id} disabled={editingId !== null} />
                            <span className="text-xs text-gray-400 font-mono w-10 shrink-0">{g.groupCode ?? g.sapGroupCode}</span>
                            <span className="text-sm text-gray-900">{g.name}</span>
                            {g.description && <span className="text-xs text-gray-400">{g.description}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <TenantToggleChips group={g} />
                            {g.tanssLinkTypeId && (
                              <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', TANSS_LINK_TYPE_COLORS[g.tanssLinkTypeId])}>
                                {TANSS_LINK_TYPE_LABELS[g.tanssLinkTypeId]}{g.tanssTypeId ? ` (${g.tanssTypeId})` : ''}
                              </span>
                            )}
                            {g.requiresSerialNumber && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-medium">SN</span>}
                            {!g.requiresSerialNumber && g.serialNumberMinPrice != null && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-600 font-medium">SN ab {g.serialNumberMinPrice.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €</span>}
                            {g.isCustomerProperty && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Kd</span>}
                            {(g.minStock > 0 || g.maxStock > 0) && <span className="text-xs text-gray-400">{g.minStock}/{g.maxStock}</span>}
                            <SapDefaultsChips group={g} />
                            <span className="text-xs text-gray-400">{g.articleCount} Art.</span>
                            {tenantSapMap[g.id]?.length ? (
                              <span className="text-xs text-gray-300 font-mono">
                                {tenantSapMap[g.id].map(t => `${t.tenantCode} #${t.sapCode}`).join(' · ')}
                              </span>
                            ) : g.sapGroupCode != null ? (
                              <span className="text-xs text-gray-300 font-mono">SAP #{g.sapGroupCode}</span>
                            ) : null}
                            <button
                              onClick={(e) => { e.stopPropagation(); openDeleteDialog(g) }}
                              className="ml-1 p-0.5 text-gray-300 hover:text-red-500 transition-colors"
                              title="Gruppe löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </DroppableSection>
              )}

              {/* Collapsed/empty: show tiny drop zone during drag */}
              {(isCollapsed || section.items.length === 0) && section.title && draggingGroup && (
                <DroppableSection id={dropId} className="h-8 flex items-center justify-center text-xs text-gray-400 border-t border-dashed border-gray-200 bg-gray-50/50">
                  Hier ablegen
                </DroppableSection>
              )}

              {/* Inline create form for new child group */}
              {!isCollapsed && creatingSection === sectionKey && (
                <div className="border-t border-brand-100 border-l-2 border-l-brand-300">
                  <div className="flex items-center gap-2 pl-8 pr-4 py-2 bg-brand-50">
                    <input
                      type="number"
                      autoFocus
                      value={createForm.groupCode}
                      onChange={(e) => setCreateForm((f) => ({ ...f, groupCode: e.target.value }))}
                      placeholder="Code (z.B. 1011)"
                      className="w-28 text-sm border border-gray-300 rounded px-2 py-1 font-mono"
                    />
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(false); if (e.key === 'Escape') setCreatingSection(null) }}
                      placeholder="Name der Untergruppe"
                      className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                    />
                    <button onClick={() => submitCreate(false)} disabled={createMutation.isPending || !createForm.name || !createForm.groupCode} className="flex items-center gap-1 px-2 py-1 text-xs bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50">
                      {createMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Erstellen
                    </button>
                    <button onClick={() => setCreatingSection(null)} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"><X className="w-3 h-3" /></button>
                  </div>
                  {createMutation.isError && (
                    <p className="pl-8 pb-2 text-xs text-red-600 font-mono">{getErrorMessage(createMutation.error as Error)}</p>
                  )}
                </div>
              )}

              {/* Leaf section (header with no children) – show summary inline */}
              {!isCollapsed && section.title && section.items.length === 0 && editingId !== section.title.id && !draggingGroup && creatingSection !== sectionKey && (
                <div className="pl-8 pr-4 py-2 border-l-2 border-l-brand-100 text-xs text-gray-400 italic">
                  Keine Untergruppen
                </div>
              )}
            </div>
          )
        })}
      </div>

      <DragOverlay>
        {draggingGroup && (
          <div className="bg-white border border-brand-400 rounded-lg px-4 py-2 shadow-lg text-sm font-medium text-gray-900 flex items-center gap-2 opacity-90">
            <GripVertical className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-400 font-mono">{draggingGroup.groupCode ?? draggingGroup.sapGroupCode}</span>
            {draggingGroup.name}
          </div>
        )}
      </DragOverlay>
      </DndContext>

      {/* Delete group dialog */}
      {deletingGroup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeletingGroup(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg space-y-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Gruppe löschen</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  <span className="font-mono text-gray-400 mr-1">{deletingGroup.groupCode}</span>
                  {deletingGroup.name}
                </p>
              </div>
              <button onClick={() => setDeletingGroup(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {/* Article list */}
            {groupArticlesQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Artikel werden geladen...
              </div>
            )}

            {groupArticlesQuery.data && groupArticlesQuery.data.length === 0 && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                Gruppe hat keine Artikel — kann direkt gelöscht werden.
              </p>
            )}

            {groupArticlesQuery.data && groupArticlesQuery.data.length > 0 && (
              <div className="space-y-3">
                <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2">
                  <p className="text-sm font-medium text-amber-900">{groupArticlesQuery.data.length} Artikel müssen umgebucht werden</p>
                  <p className="text-xs text-amber-700 mt-0.5">Wähle die Zielgruppe, in die alle Artikel verschoben werden sollen.</p>
                </div>

                {/* Article list (max 8 visible) */}
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 text-xs">
                  {groupArticlesQuery.data.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="font-mono text-gray-400 shrink-0">{a.itemCode}</span>
                      <span className="text-gray-700 truncate">{a.itemName}</span>
                    </div>
                  ))}
                </div>

                {/* Target group selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Zielgruppe</label>
                  <select
                    value={deleteTargetGroupId}
                    onChange={(e) => setDeleteTargetGroupId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">— Zielgruppe wählen —</option>
                    {groups
                      .filter((g) => g.id !== deletingGroup.id && !isHeader(g))
                      .sort((a, b) => (a.groupCode ?? 0) - (b.groupCode ?? 0))
                      .map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.groupCode ? `${g.groupCode} – ` : ''}{g.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            {/* SAP toggle */}
            {deletingGroup.sapGroupCode != null && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={deleteSap} onChange={(e) => setDeleteSap(e.target.checked)} />
                <span className="text-gray-700">Auch in SAP löschen (SAP #{deletingGroup.sapGroupCode})</span>
              </label>
            )}

            {/* Info: header group still has children — cascade delete available */}
            {isHeader(deletingGroup) && (() => {
              const childCount = groups.filter(
                (g) => !isHeader(g) && g.groupCode != null && Math.floor(g.groupCode / 100) * 100 === deletingGroup.groupCode,
              ).length
              return childCount > 0 ? (
                <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2">
                  <p className="text-sm font-medium text-amber-900">
                    {childCount} Untergruppe{childCount !== 1 ? 'n' : ''} vorhanden
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Alle Untergruppen zusammen mit der Hauptgruppe löschen — nur möglich wenn keine Untergruppe Artikel enthält.
                  </p>
                </div>
              ) : null
            })()}

            {/* Errors */}
            {reassignDeleteMutation.isError && (
              <p className="text-xs text-red-600 font-mono">{getErrorMessage(reassignDeleteMutation.error)}</p>
            )}
            {emptyDeleteMutation.isError && (
              <p className="text-xs text-red-600 font-mono">{getErrorMessage(emptyDeleteMutation.error)}</p>
            )}
            {cascadeDeleteMutation.isError && (
              <p className="text-xs text-red-600 font-mono">{getErrorMessage(cascadeDeleteMutation.error)}</p>
            )}
            {reassignDeleteMutation.isSuccess && reassignDeleteMutation.data.sapErrors.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-0.5">
                <p className="font-medium">Lokal gelöscht, SAP-Fehler:</p>
                {reassignDeleteMutation.data.sapErrors.map((e, i) => <p key={i} className="font-mono">{e}</p>)}
              </div>
            )}

            {/* Actions */}
            {(() => {
              const childCount = isHeader(deletingGroup)
                ? groups.filter((g) => !isHeader(g) && g.groupCode != null && Math.floor(g.groupCode / 100) * 100 === deletingGroup.groupCode).length
                : 0
              return (
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  <button onClick={() => setDeletingGroup(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
                    Abbrechen
                  </button>
                  {childCount === 0 && groupArticlesQuery.data?.length === 0 ? (
                    <button
                      onClick={() => emptyDeleteMutation.mutate(deletingGroup.id)}
                      disabled={emptyDeleteMutation.isPending}
                      className="flex items-center gap-2 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {emptyDeleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Löschen
                    </button>
                  ) : childCount === 0 ? (
                    <button
                      onClick={() => reassignDeleteMutation.mutate({ groupId: deletingGroup.id, targetGroupId: deleteTargetGroupId, deleteSap })}
                      disabled={reassignDeleteMutation.isPending || !deleteTargetGroupId}
                      className="flex items-center gap-2 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {reassignDeleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Umbuchen & löschen
                    </button>
                  ) : (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => cascadeDeleteMutation.mutate(deletingGroup.id)}
                      disabled={cascadeDeleteMutation.isPending}
                    >
                      {cascadeDeleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      {childCount} Untergruppe{childCount !== 1 ? 'n' : ''} + Hauptgruppe löschen
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
